/* eslint-disable max-lines */
import { BrandSymbolKey, CSSObject, IconKey } from '@melio/penny';
import {
  Card,
  DeliveryMethodByPayor,
  DeliveryMethodType,
  FundingSource,
  SupportedDeliveryMethodTypeOption,
} from '@melio/platform-api';
import { SpaceApi } from '@usersnap/browser';

export type RecursivePartial<T> = {
  [P in keyof T]?: RecursivePartial<T[P]>;
};

export const accountIntegrationTypes = ['deposits', 'plaid'] as const;

export type AccountIntegrationType = (typeof accountIntegrationTypes)[number];

const cardBrands = ['visa', 'mastercard', 'american-express', 'diners-club', 'discover', 'unknown'] as const;
export type CardBrand = (typeof cardBrands)[number];

export type EOYCheck = {
  enabled: boolean;
  promoteFastCheck: boolean;
  link: string;
};

export const TrustedPartners = {
  Evolve: 'Evolve',
  Mastercard: 'Mastercard',
  Quickbooks: 'Quickbooks',
  Diners: 'Diners',
  Visa: 'Visa',
  Discover: 'Discover',
  Amex: 'Amex',
} as const;

export type TrustedPartners = (typeof TrustedPartners)[keyof typeof TrustedPartners];

export enum PaymentSchedulingPreference {
  ByScheduleDate = 'byScheduleDate',
  ByDeliveryDate = 'byDeliveryDate',
}

export enum ContextualOnboardingButtons {
  QuickBooksOnline = 'QuickBooksOnline',
  GmailSync = 'GmailSync',
  FirstPayment = 'FirstPayment',
}

export type UnilateralRequest = {
  announceFees: boolean;
};
export type FileType = 'jpg' | 'png' | 'pdf';

export type FundingSourceGroupName = FundingSource['type'] | Card['type'];
export type FundingSourceCardPolicy = {
  fundingSourceCard?: {
    showCreatedBy?: boolean;
  };
};
export type FundingSourcePolicy = {
  [key in FundingSource['type'] | Card['type']]?: {
    order?: number;
    fundingSourceGroup?: FundingSourceGroupName;
    read?: boolean;
    write?: boolean;
  };
};

export enum VendorDirectory {
  Fiserv = 'Fiserv',
}

export type LinkConstants = {
  'app.settings.SupportSection.accordion.businessBills.links.link1.href': string;
  'app.settings.SupportSection.accordion.addSparkCard.links.link1.href': string;
  'app.settings.SupportSection.accordion.syncQuickBooks.links.link1.href': string;
  'app.settings.SupportSection.accordion.addDebitCard.links.link1.href': string;
  'app.settings.SupportSection.accordion.verifyMicroDeposits.links.link1.href': string;
  'app.settings.SupportSection.accordion.addBankAccount.links.link1.href': string;
  'app.settings.SupportSection.accordion.addBill.links.link1.href': string;
  'app.settings.SupportSection.accordion.payBill.links.link1.href': string;
  'app.settings.SupportSection.accordion.deliveryTimelines.links.link1.href': string;
  'app.settings.SupportSection.accordion.vendors.links.link1.href': string;
  'app.settings.SupportSection.accordion.cancelPayment.links.link1.href': string;
  'app.settings.SupportSection.accordion.security.links.link1.href': string;
  'app.settings.SupportSection.accordion.addBillClone.links.link1.href': string;
  'app.settings.SupportSection.accordion.addBillClone.links.link2.href': string;
  'app.settings.SupportSection.accordion.addBillClone.links.link3.href': string;
  'widgets.footer.termsOfServiceLink.href': string;
  'widgets.footer.privacyPolicy.href': string;
  'widgets.companySettings.header.sectionBanner.linkHref': string;
  'activities.repaymentTerms.screens.repaymentTerms.TermsAndConditions.creditKey': string;
  'widgets.fundingSources.selectFundingSourceCard.payWithFinancing.ineligible.reason.vendor-ineligible.learnMore.href': string;
  'activities.financingApplication.screens.applicationForm.title.learnMore': string;
  'creditKey.legal.privacyPolicy': string;
  'creditKey.legal.termsOfService': string;
  helpCenter: string;
  submitSupportRequest: string;
  backButtonUrl: string;
  backButtonUrlFallback: string;
  portalLink: string;
  failedToCollectSupport: string;
  financingMoreDetails: string;
  unilateralDeliveryLearnMore: string;
  undepositedCheckLearnMore: string;
  contactCreditKey: string;
  resolveFailedPayment: string;
  contactMelioSupport: string;
};

export type DeliveryMethodTypeOptionDisabledReasonsMap = {
  [key in SupportedDeliveryMethodTypeOption['type']]: SupportedDeliveryMethodTypeOption['reason'][];
};

export type MelioConfig = {
  server: {
    apiServer: string;
  };
  settings: {
    payment: {
      scheduling: {
        scheduleBy: PaymentSchedulingPreference;
        hideScheduleDateIndication: boolean;
      };
      showCreatedByIndication: boolean;
      memoToVendor: {
        maxLength: number;
        deliveryMethodBlackList: DeliveryMethodType[];
      };
    };
    fundingSourcePolicy?: FundingSourcePolicy & FundingSourceCardPolicy;
    icons: {
      fundingSource: {
        bank: IconKey | BrandSymbolKey;
      };
      deliveryMethod: {
        virtualCard: BrandSymbolKey;
      };
    };
    assets: { [key: string]: string };
    partnerDisplayName: string;
    bankIntegrations?: AccountIntegrationType[];
    supportEmail: string;
    termsOfServiceUrl: string;
    learnMoreAccountVerificationURL: string;
    mccLearnMoreUrl: string;
    privacyPolicyUrl: string;
    goodsReceivedAmountThreshold: number;
    requireInvoiceForBillsAmountThreshold: number;
    helpCenterUrl: string;
    registerToMelioGetPaid: string;
    fileSizeLimit: number;
    fileAllowedFormats: FileType[];
    tokenProvider: 'tabapay' | 'tbt';
    eoyCheck: EOYCheck;
    trustedPartners: TrustedPartners[];
    unilateralRequest: UnilateralRequest;
    payDashboardBillsTabTourEnabled: boolean;
    newPayExperience: {
      isEnabled: boolean;
      tabsOrder: ('vendors' | 'bills' | 'payments' | 'approvals' | 'financing')[];
      firstTimeExperience: {
        assetType: 'image' | 'video';
        imageUrl: string;
        mobileImageUrl: string;
        videoLink: string;
      };
    };
    newBillExperience: {
      isRecurringEnabled: boolean;
    };
    isEmbeddedExperience: boolean;
    isPaymentApprovalLimitEnabled: boolean;
    isLineItemsEnabled: boolean;
    isMultiColoredAvatarsEnabled: boolean;
    isUnilateralDeliveryMethodSupported: boolean;
    isPaymentTimelineEnabled: boolean;
    virtualCardLearnMoreLink: string;
    contractorLearnMoreLink: string;
    tax1099LearnMoreLink: string;
    showDemoRequestIcon: boolean;
    contextualOnboarding: {
      buttons: ContextualOnboardingButtons[];
    };
    vendor: {
      collectedDetails: 'extended' | 'minimal';
      vendorSelect: {
        createVendor: {
          showVendorDetailsForm: boolean;
        };
      };
      createVendor: {
        shouldSwitchToExtendedFormOnApiErrors: boolean;
        hasSkipDeliveryMethodButton: boolean;
        canAddVendorWithTheSameName: boolean;
        companyNameFormatRegexp?: RegExp;
        companyNameMaxLength?: number;
      };
    };
    virtualCardExpiryPeriodInDays: number;
    deliveryMethodTypeOrder: Record<DeliveryMethodByPayor['type'], number>;
    deliveryMethodTypeOptionDisabledReasons: DeliveryMethodTypeOptionDisabledReasonsMap;
    syncProgressIndicationEnabled: boolean;
    vex: {
      zdSupportFormUrl: string;
      persistentFastOptOutArticleUrl: string;
      unilateral: {
        isEmailLoginEnabled: boolean;
        shouldSuggestJoinMelio: boolean;
        isNewUnilateral: boolean;
        suvcInformationLink: string;
      };
    };
    showCheckMarkIconForManagedVendor: boolean;
  };
  services: {
    forethoughtApiKey: string;
    zendeskKey: string;
    zendeskWorkflow: string;
    googleClientId: string;
    smartystreets: {
      apiKey: string;
    };
    googleMaps: {
      apiKey: string;
    };
    plaid: {
      env: string;
      apiKey: string;
    };
    tabapay: {
      card: TabapayIframe;
      credit?: TabapayIframe;
      debit?: TabapayIframe;
    };
    tbt: {
      apiKey: string;
      supportedCardProviders: { [K in CardBrand]: boolean };
      fontsUrl: string;
    };
    userSnap: {
      spaceId: string;
      projectId: string;
    };
  };
  production: boolean;
  featureFlagProvider: {
    shouldLoad: boolean;
    clientId: string;
  };
  analytics: {
    shouldTrackEvents: boolean;
    shouldPrintEvents: boolean;
    key: string;
  };
  dataDog: {
    shouldInit: boolean;
    appId: string;
    clientId: string;
    site: string;
    service: string;
  };
  tutorialVideo: string;
  zendesk: {
    useNewWidgetApi: boolean;
    headerColor: string;
    resultListsColor: string;
    themeColor: string;
    title: string;
    logo: string;
  };
  links: LinkConstants;
  sizzersApi?: {
    baseUrl: string;
    meliomeUrl: string;
  };
};

type TabapayIframe = {
  url: string;
  iframeWidth?: CSSObject['width'];
  iframeHeight?: CSSObject['height'];
};

export type InitOptions = {
  custom?: Record<string, unknown>;
  user?: {
    email?: string;
    userId?: string;
  };
  locale?: string;
  useSystemFonts?: boolean;
  useLocalStorage?: boolean;
};

export type FeedbackProviderProps = {
  feedbackWidgetProps?: {
    userEmail?: string;
    userId?: string;
    userCreatedAt?: string;
    organizationId?: string;
    isFeedbackWidgetOn?: boolean;
  };
  children?: React.ReactNode;
};

export type PartialMelioConfig = RecursivePartial<MelioConfig>;

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    onUserSnapLoad?: (api: SpaceApi) => Promise<void>;
    cy?: {
      Cypress: {
        testingType: string;
      };
    };
  }
}

export type DynamicSettingsType = {
  shouldShowContentKeys?: boolean;
};

export type DynamicSettingsProps = {
  dynamicSettings?: DynamicSettingsType;
};
